import React, {useCallback, useEffect, useRef} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {format} from 'date-fns';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {FileRejection, useDropzone} from 'react-dropzone';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchDocumentGenerateId} from 'api';
import {fetchTinLegalName} from 'api/documents/documents';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {COUNTRY_CODES} from 'core/constants/countryCodes';
import {RestError, showRestError} from 'core/errors';
import {dropOutForm$, dropOutFormChanged, stepChanged} from 'models/documents/create';
import {colors} from 'theme';
import {DropOutFormData} from 'types';
import {useDropoutDictionaryOptions} from 'utils/dropoutDictionary';

import {FormContainer, FormField, FormWrapper, NavigationButtonsContainer} from '../common/styled';

import {ResultMarkingCodes} from './ResultMarkingCodes';
import {
    AttachmentDropzone,
    AttachmentDropzoneAction,
    AttachmentDropzoneContent,
    AttachmentDropzoneHint,
    AttachmentDropzoneText,
    AttachmentFileItem,
    AttachmentFileList,
    AttachmentFileName,
    AttachmentsLabel,
} from './styled';

const COUNTERPARTY_REASONS = ['SALE_INTERNAL', 'FREE_TRANSFER', 'SAMPLING'];
const PROCESSING_REASONS = ['PROCESSING', 'REPACKING'];
const SUPPORTING_DOCUMENT_REQUIRED_REASONS = [
    'EXPORT',
    'DESTRUCTION',
    'CONFISCATION',
    'PRODUCT_RECALL',
    'LABORATORY',
    'PROCESSING',
];
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.docx';
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;

const schema = yup.object().shape({
    reason: yup.string().required(i18next.t('components.form.required')),
    supportingDocumentNumber: yup
        .string()
        .trim()
        .when('reason', {
            is: (reason: string) => SUPPORTING_DOCUMENT_REQUIRED_REASONS.includes(reason),
            then: (value) => value.required(i18next.t('components.form.required')),
            otherwise: (value) => value.notRequired(),
        }),
    supportingDocumentDate: yup
        .date()
        .nullable()
        .required(i18next.t('components.form.required')),
    salesChannel: yup.string().when('reason', {
        is: 'RETAIL',
        then: (value) => value.required(i18next.t('components.form.required')),
        otherwise: (value) => value.notRequired(),
    }),
    counterpartyId: yup.string().when('reason', {
        is: (reason: string) => COUNTERPARTY_REASONS.includes(reason),
        then: (value) => value.required(i18next.t('components.form.required')),
        otherwise: (value) => value.notRequired(),
    }),
    counterpartyName: yup.string().when('reason', {
        is: (reason: string) => COUNTERPARTY_REASONS.includes(reason),
        then: (value) => value.required(i18next.t('components.form.required')),
        otherwise: (value) => value.notRequired(),
    }),
    destinationCountry: yup.string().when('reason', {
        is: 'EXPORT',
        then: (value) => value.required(i18next.t('components.form.required')),
        otherwise: (value) => value.notRequired(),
    }),
    resultMarkingCodes: yup.array().when('reason', {
        is: (reason: string) => PROCESSING_REASONS.includes(reason),
        then: (value) => value.min(1, i18next.t('components.form.required')),
        otherwise: (value) => value.notRequired(),
    }),
});

const getError = (message?: string) =>
    message ? <span style={{fontSize: 12, color: 'red'}}>{i18next.t(message)}</span> : null;

const getRootFieldError = (error: unknown) => error as FieldError | undefined;

interface AttachmentsFieldProps {
    value: File[];
    onChange: (files: File[]) => void;
    onInvalid: () => void;
    onValid: () => void;
}

const AttachmentsField = ({value, onChange, onInvalid, onValid}: AttachmentsFieldProps) => {
    const {t} = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length) {
                onInvalid();
                return;
            }

            const nextFiles = [...value, ...acceptedFiles].filter(
                (file, index, files) =>
                    files.findIndex(
                        (candidate) =>
                            candidate.name === file.name &&
                            candidate.size === file.size &&
                            candidate.lastModified === file.lastModified,
                    ) === index,
            );

            if (nextFiles.length > MAX_ATTACHMENTS) {
                onInvalid();
                return;
            }

            onChange(nextFiles);
            onValid();
        },
        [onChange, onInvalid, onValid, value],
    );
    const {getInputProps, getRootProps, isDragActive} = useDropzone({
        accept: ATTACHMENT_ACCEPT,
        maxSize: MAX_ATTACHMENT_SIZE,
        multiple: true,
        onDrop,
    });

    return (
        <>
            <AttachmentsLabel>{t('pages.documents.form.dropout.attachments')}</AttachmentsLabel>
            <AttachmentDropzone isDragActive={isDragActive} {...getRootProps()}>
                <input {...getInputProps()} />
                <AttachmentDropzoneContent>
                    <Icon
                        source={UploadIcon}
                        width={24}
                        height={24}
                        fillColor={colors.brandPrimary}
                    />
                    <AttachmentDropzoneText>
                        {isDragActive ? (
                            t('pages.documents.form.dropout.dropAttachmentsHere')
                        ) : (
                            <>
                                {t('pages.documents.form.dropout.dragAndDropAttachments')}{' '}
                                <AttachmentDropzoneAction>
                                    {t('pages.documents.form.dropout.chooseFiles')}
                                </AttachmentDropzoneAction>
                            </>
                        )}
                    </AttachmentDropzoneText>
                    <AttachmentDropzoneHint>
                        {t('pages.documents.form.dropout.attachmentsHint')}
                    </AttachmentDropzoneHint>
                </AttachmentDropzoneContent>
            </AttachmentDropzone>
            <AttachmentFileList>
                {value.map((file) => (
                    <AttachmentFileItem key={`${file.name}-${file.lastModified}`}>
                        <AttachmentFileName title={file.name}>{file.name}</AttachmentFileName>
                        <IconButton
                            label={t('pages.documents.form.dropout.removeAttachment')}
                            icon={
                                <Icon
                                    source={CrossIcon}
                                    width={10}
                                    height={10}
                                    fillColor={colors.textPrimary}
                                />
                            }
                            onClick={() => {
                                onChange(value.filter((item) => item !== file));
                                onValid();
                            }}
                        />
                    </AttachmentFileItem>
                ))}
            </AttachmentFileList>
        </>
    );
};

export const DocumentForm = () => {
    const {t} = useTranslation();
    const {reasons, salesChannels} = useDropoutDictionaryOptions();
    const dropOutForm = useStore(dropOutForm$);
    const [isDocumentIdLoading, setDocumentIdLoading] = React.useState(false);
    const counterpartyCheckIdRef = useRef(0);
    const {clearErrors, control, handleSubmit, formState, setError, setValue, watch} = useForm<
        DropOutFormData
    >({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: dropOutForm,
    });
    const reason = watch('reason');

    useEffect(() => {
        if (dropOutForm.documentId) {
            setValue('documentId', dropOutForm.documentId);
            return;
        }

        let cancelled = false;
        setDocumentIdLoading(true);
        fetchDocumentGenerateId()
            .then((documentId) => {
                if (!cancelled) setValue('documentId', documentId);
            })
            .catch((error) => console.error(error))
            .finally(() => {
                if (!cancelled) setDocumentIdLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [dropOutForm.documentId, setValue]);

    const onReasonChange = (nextReason: string) => {
        counterpartyCheckIdRef.current += 1;
        clearErrors('counterpartyId');
        setValue('reason', nextReason, {shouldValidate: true});
        setValue('salesChannel', '', {shouldValidate: true});
        setValue('counterpartyId', '', {shouldValidate: true});
        setValue('counterpartyName', '', {shouldValidate: true});
        setValue('destinationCountry', '', {shouldValidate: true});
        setValue('resultMarkingCodes', [], {shouldValidate: true});
    };

    const validateCounterpartyTin = async (tin: string, currentReason: string) => {
        const normalizedTin = tin.trim().toLowerCase();

        if (!COUNTERPARTY_REASONS.includes(currentReason) || !normalizedTin) return true;

        const checkId = ++counterpartyCheckIdRef.current;

        try {
            const organizations = await fetchTinLegalName({combinedSearch: tin.trim()});

            if (checkId !== counterpartyCheckIdRef.current) return false;

            const isRegistered = organizations.some(
                (organization) => organization.taxIdNumber?.trim().toLowerCase() === normalizedTin,
            );

            if (isRegistered) {
                setError('counterpartyId', {
                    type: 'validate',
                    message: 'pages.documents.form.dropout.registeredCounterparty',
                });
                return false;
            }

            clearErrors('counterpartyId');
            return true;
        } catch (error) {
            if (checkId === counterpartyCheckIdRef.current) showRestError(error as RestError);
            return false;
        }
    };

    const onHandleChanged = async (data: DropOutFormData) => {
        const isCounterpartyValid = await validateCounterpartyTin(
            data.counterpartyId ?? '',
            data.reason,
        );

        if (!isCounterpartyValid) return;

        stepChanged(1);
        dropOutFormChanged(data);
    };

    const isRetail = reason === 'RETAIL';
    const hasCounterparty = COUNTERPARTY_REASONS.includes(reason);
    const isExport = reason === 'EXPORT';
    const isProcessing = PROCESSING_REASONS.includes(reason);
    const isSupportingDocumentRequired = SUPPORTING_DOCUMENT_REQUIRED_REASONS.includes(reason);
    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <FormWrapper>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="reason"
                                    render={({value}, {invalid}) => (
                                        <Select
                                            label={t('pages.documents.form.dropout.reason')}
                                            isRequired
                                            value={reasons.find((option) => option.value === value)}
                                            options={reasons}
                                            hasError={invalid}
                                            onChange={(option: SelectOption<string> | null) => {
                                                if (option?.value) onReasonChange(option.value);
                                            }}
                                        />
                                    )}
                                />
                                {getError(formState.errors.reason?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Input
                                    label={t('pages.documents.form.dropout.documentDate')}
                                    value={format(new Date(), 'dd.MM.yyyy')}
                                    disabled
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="documentId"
                                    render={({value}) => (
                                        <Input
                                            label={t('pages.documents.form.dropout.documentNumber')}
                                            value={value ?? ''}
                                            disabled
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="supportingDocumentNumber"
                                    render={({value, onChange}, {invalid}) => (
                                        <Input
                                            label={t(
                                                'pages.documents.form.dropout.supportingDocumentNumber',
                                            )}
                                            isRequired={isSupportingDocumentRequired}
                                            value={value ?? ''}
                                            onChange={onChange}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                                {getError(formState.errors.supportingDocumentNumber?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="supportingDocumentDate"
                                    render={({value, onChange}) => (
                                        <DatePicker
                                            label={t(
                                                'pages.documents.form.dropout.supportingDocumentDate',
                                            )}
                                            isRequired
                                            maxDate={new Date()}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.supportingDocumentDate}
                                        />
                                    )}
                                />
                                {getError(formState.errors.supportingDocumentDate?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="comment"
                                    render={({value, onChange}) => (
                                        <Input
                                            label={t('pages.documents.form.dropout.comment')}
                                            value={value ?? ''}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        {isRetail && (
                            <Grid item xs={12} md={6}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="salesChannel"
                                        render={({value, onChange}, {invalid}) => (
                                            <Select
                                                label={t('pages.documents.form.salesChannel')}
                                                isRequired
                                                value={salesChannels.find(
                                                    (option) => option.value === value,
                                                )}
                                                options={salesChannels}
                                                hasError={invalid}
                                                onChange={(option: SelectOption<string> | null) =>
                                                    onChange(option?.value ?? '')
                                                }
                                            />
                                        )}
                                    />
                                    {getError(formState.errors.salesChannel?.message)}
                                </FormField>
                            </Grid>
                        )}
                        {isExport && (
                            <Grid item xs={12} md={6}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="destinationCountry"
                                        render={({value, onChange}, {invalid}) => (
                                            <Select
                                                label={t(
                                                    'pages.documents.form.dropout.destinationCountry',
                                                )}
                                                isRequired
                                                value={COUNTRY_CODES.find(
                                                    (option) => option.value === value,
                                                )}
                                                options={COUNTRY_CODES}
                                                hasError={invalid}
                                                onChange={(option: SelectOption<string> | null) =>
                                                    onChange(option?.value ?? '')
                                                }
                                            />
                                        )}
                                    />
                                    {getError(formState.errors.destinationCountry?.message)}
                                </FormField>
                            </Grid>
                        )}
                        {hasCounterparty && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormField>
                                        <Controller
                                            control={control}
                                            name="counterpartyId"
                                            render={({value, onChange}, {invalid}) => (
                                                <Input
                                                    label={t(
                                                        'pages.documents.form.dropout.counterpartyId',
                                                    )}
                                                    isRequired
                                                    value={value ?? ''}
                                                    onChange={(event) => {
                                                        counterpartyCheckIdRef.current += 1;
                                                        clearErrors('counterpartyId');
                                                        onChange(event);
                                                    }}
                                                    onBlur={() =>
                                                        validateCounterpartyTin(value ?? '', reason)
                                                    }
                                                    hasError={invalid}
                                                />
                                            )}
                                        />
                                        {getError(formState.errors.counterpartyId?.message)}
                                    </FormField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormField>
                                        <Controller
                                            control={control}
                                            name="counterpartyName"
                                            render={({value, onChange}, {invalid}) => (
                                                <Input
                                                    label={t(
                                                        'pages.documents.form.dropout.counterpartyName',
                                                    )}
                                                    isRequired
                                                    value={value ?? ''}
                                                    onChange={onChange}
                                                    hasError={invalid}
                                                />
                                            )}
                                        />
                                        {getError(formState.errors.counterpartyName?.message)}
                                    </FormField>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormField>
                                        <Controller
                                            control={control}
                                            name="attachmentFiles"
                                            render={({value = [], onChange}) => (
                                                <AttachmentsField
                                                    value={value}
                                                    onChange={onChange}
                                                    onInvalid={() =>
                                                        setError('attachmentFiles', {
                                                            message:
                                                                'pages.documents.form.dropout.invalidAttachments',
                                                        })
                                                    }
                                                    onValid={() => clearErrors('attachmentFiles')}
                                                />
                                            )}
                                        />
                                        {getError(
                                            getRootFieldError(formState.errors.attachmentFiles)
                                                ?.message,
                                        )}
                                    </FormField>
                                </Grid>
                            </Grid>
                        </Grid>
                        {isProcessing && (
                            <Grid item xs={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="resultMarkingCodes"
                                        render={({value, onChange}) => (
                                            <ResultMarkingCodes
                                                value={value ?? []}
                                                onChange={onChange}
                                            />
                                        )}
                                    />
                                    {getError(
                                        getRootFieldError(formState.errors.resultMarkingCodes)
                                            ?.message,
                                    )}
                                </FormField>
                            </Grid>
                        )}
                    </Grid>
                </FormWrapper>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={
                            !formState.isValid || isDocumentIdLoading || formState.isSubmitting
                        }
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
